/**
 * Theme Context
 * Manages theme preferences including density/scale settings
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import baseTheme from '../theme';
import { components } from '../theme/components';
import { getDensity, applyDensity, getAutoDensity, densityPresets } from '../theme/density';

const ThemeContext = createContext();

export const useThemeSettings = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeSettings must be used within ThemeSettingsProvider');
  }
  return context;
};

export function ThemeSettingsProvider({ children }) {
  // Get saved density or auto-detect
  const savedDensity = localStorage.getItem('app-density') || 'comfortable';
  const [density, setDensity] = useState(savedDensity);
  
  // Create theme with current density
  const theme = useMemo(() => {
    const densityConfig = getDensity(density);
    
    // Create new theme with density applied
    const newTheme = createTheme({
      ...baseTheme,
      typography: {
        ...baseTheme.typography,
        fontSize: 12 * densityConfig.scale,
        body1: {
          fontSize: densityConfig.typography.body1,
        },
        body2: {
          fontSize: densityConfig.typography.body2,
        },
      },
      spacing: (factor) => {
        if (typeof factor === 'number') {
          return `${factor * densityConfig.spacing.unit}px`;
        }
        return baseTheme.spacing(factor);
      },
    });
    
    // Add custom properties
    newTheme.typography.fontSize = densityConfig.typography.fontSize;
    newTheme.dimensions = {
      ...baseTheme.dimensions,
      ...densityConfig.components,
    };
    newTheme.density = density;
    newTheme.densityScale = densityConfig.scale;
    
    // Copy mixins from base theme
    newTheme.mixins = baseTheme.mixins;
    
    // Apply component overrides with density-aware theme
    newTheme.components = components(newTheme);
    
    return newTheme;
  }, [density]);
  
  // Save density preference
  useEffect(() => {
    localStorage.setItem('app-density', density);
  }, [density]);
  
  const changeDensity = (newDensity) => {
    if (densityPresets[newDensity]) {
      setDensity(newDensity);
    }
  };
  
  const resetDensity = () => {
    const autoDensity = getAutoDensity();
    setDensity(autoDensity);
  };
  
  const value = {
    density,
    setDensity: changeDensity,
    resetDensity,
    densityOptions: Object.keys(densityPresets).map(key => ({
      value: key,
      ...densityPresets[key],
    })),
    theme,
  };
  
  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}